/**
 * DashboardPage — shadcn/ui v4 section-cards reference layout.
 *
 * Layout: @container/main with per-section px-4 lg:px-6 padding.
 * KPI grid: container-query responsive (1 → 2 → 4 cols).
 * Charts: functional 7d / 30d / 90d Tabs toggle (re-fetches from API).
 * Agents + CSAT: uses dedicated TopAgentsTable / CSATCard sub-components.
 *
 * No API calls, business logic, or data shapes were changed.
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw,
  Clock,
  CheckCircle2,
  MessageSquare,
  AlertCircle,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { ResponsivePie } from "@nivo/pie";
import { reportsAPI } from "../services/api";
import { AIDashboard } from "../components/AI/AIDashboard";
import EnhancedMetricCard from "../components/Dashboard/EnhancedMetricCard";
import TopAgentsTable from "../components/Dashboard/TopAgentsTable";
import CSATCard from "../components/Dashboard/CSATCard";
import {
  DashboardMetrics,
  ConversationTrendData,
  ChannelDistribution,
  AgentPerformanceMetric,
  CSATOverview,
} from "../types/dashboard";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardAction,
  CardFooter,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { cn } from "../lib/utils";

// ─── Chart colors — resolved CSS token values for Nivo (requires strings) ──────
const CHART_COLORS = [
  "hsl(221.2, 83.2%, 53.3%)",  // chart-1 blue
  "hsl(142.1, 76.2%, 36.3%)",  // chart-2 emerald
  "hsl(43, 96%, 56%)",          // chart-3 amber
  "hsl(0, 84.2%, 60.2%)",       // chart-4 red
  "hsl(280, 65%, 55%)",         // chart-5 violet
];

// KPI card color map — drives dot + sparkline stroke
const KPI_COLORS = {
  open:       "hsl(221.2, 83.2%, 53.3%)",
  pending:    "hsl(43, 96%, 56%)",
  unassigned: "hsl(0, 84.2%, 60.2%)",
  resolution: "hsl(142.1, 76.2%, 36.3%)",
};

type Range = '7' | '30' | '90';

// ─── DashboardPage ─────────────────────────────────────────────────────────────
const DashboardPage: React.FC = () => {
  const navigate = useNavigate();

  const [range, setRange]           = useState<Range>('30');
  const [loading, setLoading]       = useState<boolean>(true);
  const [error, setError]           = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const [metrics, setMetrics]                   = useState<DashboardMetrics | null>(null);
  const [conversationTrends, setConversationTrends] = useState<ConversationTrendData[]>([]);
  const [channelDistribution, setChannelDistribution] = useState<ChannelDistribution[]>([]);
  const [topAgents, setTopAgents]               = useState<AgentPerformanceMetric[]>([]);
  const [csatOverview, setCSATOverview]         = useState<CSATOverview | null>(null);

  const getDateRange = (days: number) => ({
    start_date: new Date(Date.now() - days * 86_400_000).toISOString().split("T")[0],
    end_date:   new Date().toISOString().split("T")[0],
  });

  const fetchDashboardData = async (days: number = 30) => {
    setLoading(true);
    setError(null);
    try {
      const dateRange = getDateRange(days);
      const [metricsData, trendsData, channelsData, agentsData, csatData] =
        await Promise.all([
          reportsAPI.getDashboardMetrics(),
          reportsAPI.getConversationTrends(dateRange),
          reportsAPI.getChannelDistribution(dateRange),
          reportsAPI.getTopAgents(dateRange, 5),
          reportsAPI.getCSATOverview(dateRange),
        ]);

      setMetrics(metricsData);
      setConversationTrends(
        Array.isArray(trendsData)
          ? [...trendsData].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
          : []
      );
      setChannelDistribution(channelsData);
      setTopAgents(agentsData);
      setCSATOverview(csatData);
      setLastUpdated(new Date());
    } catch (err: any) {
      console.error("Dashboard load error:", err);
      setError(err.message || "Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch whenever range changes (also fires on initial mount)
  useEffect(() => {
    fetchDashboardData(parseInt(range));
    const interval = setInterval(() => fetchDashboardData(parseInt(range)), 60_000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const generateSparklineData = (key: "new_conversations" | "resolved_conversations") =>
    conversationTrends.map((item) => ({ value: item[key] || 0 }));

  // ── Skeleton ───────────────────────────────────────────────────────────────
  if (loading && !metrics) {
    return (
      <div className="@container/main flex flex-1 flex-col gap-4 py-4 md:gap-6 md:py-6">
        {/* Header */}
        <div className="flex items-center justify-between px-4 lg:px-6">
          <div className="space-y-1.5">
            <Skeleton className="h-7 w-32" />
            <Skeleton className="h-4 w-44" />
          </div>
          <Skeleton className="h-8 w-8 rounded-md" />
        </div>
        {/* KPI cards */}
        <div className="grid grid-cols-1 gap-4 px-4 lg:px-6 @xl/main:grid-cols-2 @5xl/main:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-36 rounded-lg" />)}
        </div>
        {/* AI section */}
        <Skeleton className="mx-4 h-40 rounded-lg lg:mx-6" />
        {/* Charts */}
        <div className="grid grid-cols-1 gap-4 px-4 lg:px-6 lg:grid-cols-12">
          <Skeleton className="h-80 rounded-lg lg:col-span-8" />
          <Skeleton className="h-80 rounded-lg lg:col-span-4" />
        </div>
        {/* Agents + CSAT */}
        <div className="grid grid-cols-1 gap-4 px-4 lg:px-6 lg:grid-cols-12">
          <Skeleton className="h-64 rounded-lg lg:col-span-8" />
          <Skeleton className="h-64 rounded-lg lg:col-span-4" />
        </div>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (error && !metrics) {
    return (
      <div className="px-4 py-4 lg:px-6">
        <div className="flex items-center justify-between rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <p className="text-sm text-destructive">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchDashboardData(parseInt(range))}
            className="border-destructive/30 text-destructive hover:bg-destructive/10"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <div className="@container/main flex flex-1 flex-col gap-4 py-4 md:gap-6 md:py-6">

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 lg:px-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </p>
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={() => fetchDashboardData(parseInt(range))}
          disabled={loading}
          className="h-8 w-8"
        >
          <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
        </Button>
      </div>

      {/* ── KPI Cards — container-query responsive grid ───────────────────── */}
      <div className="grid grid-cols-1 gap-4 px-4 lg:px-6 @xl/main:grid-cols-2 @5xl/main:grid-cols-4">
        <EnhancedMetricCard
          title="Open Conversations"
          value={metrics?.open_conversations || 0}
          color={KPI_COLORS.open}
          icon={<MessageSquare />}
          sparklineData={generateSparklineData("new_conversations")}
          trend={5.2}
          trendDirection="up"
        />
        <EnhancedMetricCard
          title="Pending"
          value={metrics?.pending_conversations || 0}
          color={KPI_COLORS.pending}
          icon={<Clock />}
          sparklineData={conversationTrends.map((item) => ({
            value: item.new_conversations || 0,
          }))}
          trend={-2.4}
          trendDirection="down"
        />
        <EnhancedMetricCard
          title="Unassigned"
          value={metrics?.unassigned_conversations || 0}
          color={KPI_COLORS.unassigned}
          icon={<AlertCircle />}
          sparklineData={conversationTrends.map((item) => ({
            value: Math.floor((item.new_conversations || 0) * 0.3),
          }))}
          trend={0.8}
          trendDirection="up"
        />
        <EnhancedMetricCard
          title="Resolution Rate"
          value={Math.round(metrics?.resolution_rate || 0)}
          color={KPI_COLORS.resolution}
          icon={<CheckCircle2 />}
          suffix="%"
          sparklineData={generateSparklineData("resolved_conversations")}
          trend={3.1}
          trendDirection="up"
        />
      </div>

      {/* ── AI Dashboard ─────────────────────────────────────────────────── */}
      <div className="px-4 lg:px-6">
        <AIDashboard />
      </div>

      {/* ── Charts row ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 px-4 lg:px-6 lg:grid-cols-12">

        {/* Conversation Trends (8/12 cols) */}
        <Card className="lg:col-span-8 shadow-sm">
          <CardHeader className="pb-2">
            <div>
              <CardTitle>Conversation Trends</CardTitle>
              <CardDescription>New and resolved · Last {range} days</CardDescription>
            </div>
            <CardAction>
              <Tabs
                value={range}
                onValueChange={(v) => setRange(v as Range)}
              >
                <TabsList className="h-8 p-0.5">
                  <TabsTrigger value="7"  className="h-7 px-2.5 text-xs">7d</TabsTrigger>
                  <TabsTrigger value="30" className="h-7 px-2.5 text-xs">30d</TabsTrigger>
                  <TabsTrigger value="90" className="h-7 px-2.5 text-xs">90d</TabsTrigger>
                </TabsList>
              </Tabs>
            </CardAction>
          </CardHeader>
          <CardContent>
            {conversationTrends.length === 0 ? (
              <div className="flex h-64 flex-col items-center justify-center rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">No conversation data available yet</p>
                <p className="mt-1 text-xs text-muted-foreground/70">
                  Data will appear once conversations are created
                </p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={conversationTrends}>
                  <defs>
                    <linearGradient id="colorNew" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="hsl(var(--chart-1))" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorResolved" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="hsl(var(--chart-2))" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="hsl(var(--chart-2))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="date"
                    stroke="hsl(var(--muted-foreground))"
                    style={{ fontSize: "11px" }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    style={{ fontSize: "11px" }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                      fontSize: "12px",
                    }}
                    formatter={(value) => [value ?? 0, ""]}
                    labelStyle={{ fontWeight: "bold", marginBottom: "5px" }}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: "12px", fontSize: "12px" }}
                    iconType="circle"
                    iconSize={8}
                  />
                  <Area
                    type="monotone"
                    dataKey="new_conversations"
                    stroke="hsl(var(--chart-1))"
                    fillOpacity={1}
                    fill="url(#colorNew)"
                    name="New Conversations"
                    strokeWidth={2}
                    animationDuration={800}
                  />
                  <Area
                    type="monotone"
                    dataKey="resolved_conversations"
                    stroke="hsl(var(--chart-2))"
                    fillOpacity={1}
                    fill="url(#colorResolved)"
                    name="Resolved"
                    strokeWidth={2}
                    animationDuration={800}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
          <CardFooter className="border-t border-border pt-3 text-xs text-muted-foreground">
            Showing conversation activity for the last {range} days
          </CardFooter>
        </Card>

        {/* Channel Distribution (4/12 cols) */}
        <Card className="lg:col-span-4 bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle>Channel Distribution</CardTitle>
            <CardDescription>By conversation volume</CardDescription>
          </CardHeader>
          <CardContent>
            {!Array.isArray(channelDistribution) || channelDistribution.length === 0 ? (
              <div className="flex h-[280px] flex-col items-center justify-center rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">No channel data yet</p>
              </div>
            ) : (
              <div className="h-[280px]">
                <ResponsivePie
                  data={channelDistribution.map((item, index) => ({
                    id:    item.channel_name,
                    label: item.channel_name,
                    value: item.total_conversations,
                    color: CHART_COLORS[index % CHART_COLORS.length],
                  }))}
                  margin={{ top: 20, right: 60, bottom: 60, left: 60 }}
                  innerRadius={0.55}
                  padAngle={0.5}
                  cornerRadius={3}
                  activeOuterRadiusOffset={6}
                  borderWidth={1}
                  borderColor={{ from: "color", modifiers: [["darker", 0.15]] }}
                  arcLinkLabelsSkipAngle={10}
                  arcLinkLabelsTextColor="hsl(215.4, 16.3%, 46.9%)"
                  arcLinkLabelsThickness={1.5}
                  arcLinkLabelsColor={{ from: "color" }}
                  arcLabelsSkipAngle={10}
                  arcLabelsTextColor={{ from: "color", modifiers: [["darker", 2]] }}
                  colors={{ datum: "data.color" }}
                  enableArcLabels={true}
                  enableArcLinkLabels={true}
                  animate={true}
                  motionConfig="gentle"
                  transitionMode="innerRadius"
                  tooltip={({ datum }) => (
                    <div className="rounded-md border border-border bg-popover px-3 py-2 shadow-md">
                      <p className="text-sm font-medium text-foreground">{datum.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {datum.value} conversations (
                        {(
                          (datum.value /
                            channelDistribution.reduce(
                              (sum, item) => sum + item.total_conversations,
                              0
                            )) * 100
                        ).toFixed(1)}
                        %)
                      </p>
                    </div>
                  )}
                />
              </div>
            )}
          </CardContent>
        </Card>

      </div>

      {/* ── Top Agents + CSAT ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 px-4 lg:px-6 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <TopAgentsTable agents={topAgents} />
        </div>
        <div className="lg:col-span-4">
          <CSATCard csatOverview={csatOverview} />
        </div>
      </div>

      {/* ── Quick Actions ────────────────────────────────────────────────── */}
      <div className="px-4 pb-4 lg:px-6 lg:pb-6">
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Jump to commonly used sections</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/dashboard/conversations")}
              >
                View All Conversations
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/dashboard/contacts")}
              >
                Manage Contacts
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/dashboard/reports")}
              >
                Create Report
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/dashboard/business-hours")}
              >
                Set Business Hours
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

    </div>
  );
};

export default DashboardPage;
